import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles, Users, Globe, ChevronRight, Zap, Camera, UserSquare } from 'lucide-react';
import Footer from '../Footer';

const FEATURES = [
  {
    id: 'video-agent',
    icon: Sparkles,
    colorClass: 'text-violet-600',
    bgClass: 'bg-violet-100',
    borderClass: 'border-violet-300',
    accentBar: 'bg-gradient-to-r from-violet-500 to-purple-600',
    title: 'Video Agent',
    badge: '⚡ Easiest',
    badgeClass: 'bg-violet-100 text-violet-700',
    tagline: 'Fastest way — just type a prompt',
    description: 'Describe your video in plain text. AI picks the avatar, voice and style automatically. Zero setup needed.',
    steps: ['Type your prompt', 'AI generates the video', 'Download in minutes'],
    path: '/creator-ai/video-agent',
  },
  {
    id: 'avatar-video',
    icon: Users,
    colorClass: 'text-blue-600',
    bgClass: 'bg-blue-100',
    borderClass: 'border-blue-300',
    accentBar: 'bg-gradient-to-r from-blue-500 to-cyan-500',
    title: 'Avatar Video',
    badge: '🎭 Most Control',
    badgeClass: 'bg-blue-100 text-blue-700',
    tagline: 'Pick avatar + write your script',
    description: 'Browse 1,000+ AI avatars, write your script and choose a voice to get a professional talking-head video.',
    steps: ['Pick an avatar', 'Write your script', 'Choose voice & generate'],
    path: '/creator-ai/avatar-video',
  },
  {
    id: 'video-translate',
    icon: Globe,
    colorClass: 'text-emerald-600',
    bgClass: 'bg-emerald-100',
    borderClass: 'border-emerald-300',
    accentBar: 'bg-gradient-to-r from-emerald-500 to-teal-500',
    title: 'Video Translate',
    badge: '🌍 178 Languages',
    badgeClass: 'bg-emerald-100 text-emerald-700',
    tagline: 'Dub any video to any language',
    description: 'Paste a video URL, pick a language and our AI re-generates the voice with matching lip movements.',
    steps: ['Paste video URL', 'Choose language', 'Get dubbed video'],
    path: '/creator-ai/video-translate',
  },
  {
    id: 'photo-avatar',
    icon: Camera,
    colorClass: 'text-pink-600',
    bgClass: 'bg-pink-100',
    borderClass: 'border-pink-300',
    accentBar: 'bg-gradient-to-r from-pink-500 to-rose-500',
    title: 'Photo Avatar',
    badge: '📸 Your Face',
    badgeClass: 'bg-pink-100 text-pink-700',
    tagline: 'Animate any photo to speak',
    description: 'Upload a photo of yourself or anyone. Write a script and watch the photo come alive with realistic speech and lip movements.',
    steps: ['Upload a photo', 'Write your script', 'Get animated video'],
    path: '/creator-ai/photo-avatar',
  },
  {
    id: 'digital-twin',
    icon: UserSquare,
    colorClass: 'text-indigo-600',
    bgClass: 'bg-indigo-100',
    borderClass: 'border-indigo-300',
    accentBar: 'bg-gradient-to-r from-indigo-500 to-purple-600',
    title: 'Digital Twin',
    badge: '🧬 Pro Avatar',
    badgeClass: 'bg-indigo-100 text-indigo-700',
    tagline: 'Create your own reusable AI avatar',
    description: 'Train a hyper-realistic Digital Twin of yourself using video footage. Use it for unlimited future videos.',
    steps: ['Provide training footage', 'AI trains your Twin', 'Generate unlimited videos'],
    path: '/creator-ai/digital-twin',
  },
  {
    id: 'avatar-motion',
    icon: Zap,
    colorClass: 'text-amber-600',
    bgClass: 'bg-amber-100',
    borderClass: 'border-amber-300',
    accentBar: 'bg-gradient-to-r from-amber-500 to-orange-500',
    title: 'Avatar Motion Studio',
    badge: '🎬 Motion Transfer',
    badgeClass: 'bg-amber-100 text-amber-700',
    tagline: 'Make any photo perform any movement',
    description: 'Upload a character image and a reference motion video. AI transfers the exact movements onto your character — dance, wave, walk, anything.',
    steps: ['Upload character image', 'Provide motion video URL', 'Get animated video'],
    path: '/creator-ai/avatar-motion',
  },
];

const AIVideoHub = () => {
  const navigate = useNavigate();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Sticky Header */}
      <div className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-gray-500 hover:text-gray-900 mb-2 text-xs sm:text-sm"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Creator AI
          </button>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl flex items-center justify-center flex-shrink-0">
              <Zap className="h-4 w-4 text-yellow-400" />
            </div>
            <div>
              <h1 className="text-base font-bold text-gray-900">AI Video Studio</h1>
              <p className="text-xs text-gray-500">Professional AI videos in minutes</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">

        {/* Hero */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-yellow-100 text-yellow-800 rounded-full text-xs font-semibold mb-3">
            <Zap className="h-3.5 w-3.5" /> 200 AI Credits per video
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Create AI Videos in 5 Ways
          </h2>
          <p className="text-gray-500 text-sm max-w-md mx-auto">
            From a quick one-prompt video to your own Digital Twin — choose the method that fits your need.
          </p>
        </div>

        {/* Feature Cards — stacked on mobile */}
        <div className="flex flex-col gap-4">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div
                key={f.id}
                onClick={() => navigate(f.path)}
                className={`bg-white rounded-2xl border-2 ${f.borderClass} shadow-sm cursor-pointer hover:shadow-lg active:scale-[0.98] transition-all duration-200 overflow-hidden`}
              >
                {/* Thin accent bar */}
                <div className={`h-1.5 w-full ${f.accentBar}`} />

                {/* Card Content */}
                <div className="p-4 sm:p-5">
                  {/* Icon + Badge */}
                  <div className="flex items-center justify-between mb-3">
                    <div className={`w-10 h-10 ${f.bgClass} rounded-xl flex items-center justify-center`}>
                      <Icon className={`h-5 w-5 ${f.colorClass}`} />
                    </div>
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${f.badgeClass}`}>
                      {f.badge}
                    </span>
                  </div>

                  {/* Title + Tagline */}
                  <h3 className="text-lg font-bold text-gray-900 mb-0.5">{f.title}</h3>
                  <p className={`text-xs font-medium ${f.colorClass} mb-2`}>{f.tagline}</p>

                  {/* Description */}
                  <p className="text-sm text-gray-600 leading-relaxed mb-3">{f.description}</p>

                  {/* Steps + CTA row */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-3 border-t border-gray-100">
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      {f.steps.map((step, idx) => (
                        <div key={idx} className="flex items-center gap-1.5 text-xs text-gray-600">
                          <div className={`w-4 h-4 rounded-full ${f.accentBar} text-white flex items-center justify-center text-xs font-bold flex-shrink-0`}>
                            {idx + 1}
                          </div>
                          {step}
                        </div>
                      ))}
                    </div>
                    <div className={`flex items-center gap-1 text-sm font-semibold ${f.colorClass} flex-shrink-0`}>
                      Get Started <ChevronRight className="h-4 w-4" />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Which one to use guide */}
        <div className="mt-5 bg-white rounded-2xl border border-gray-200 p-4">
          <p className="text-sm font-semibold text-gray-700 mb-3">ℹ️ Which one should I use?</p>
          <div className="flex flex-col gap-2">
            <div className="rounded-xl p-3 bg-violet-50 border border-violet-200">
              <p className="text-xs font-semibold text-violet-800 mb-0.5">⚡ Video Agent — fastest</p>
              <p className="text-xs text-gray-600">Want a quick video without choosing avatar or voice yourself.</p>
            </div>
            <div className="rounded-xl p-3 bg-blue-50 border border-blue-200">
              <p className="text-xs font-semibold text-blue-800 mb-0.5">🎭 Avatar Video — full control</p>
              <p className="text-xs text-gray-600">Pick a specific avatar, write an exact script and control the voice.</p>
            </div>
            <div className="rounded-xl p-3 bg-emerald-50 border border-emerald-200">
              <p className="text-xs font-semibold text-emerald-800 mb-0.5">🌍 Translate — existing video</p>
              <p className="text-xs text-gray-600">You have a video and want it dubbed in another language.</p>
            </div>
            <div className="rounded-xl p-3 bg-pink-50 border border-pink-200">
              <p className="text-xs font-semibold text-pink-800 mb-0.5">📸 Photo Avatar — animate a photo</p>
              <p className="text-xs text-gray-600">Upload any photo and make it speak your script with lip-sync.</p>
            </div>
            <div className="rounded-xl p-3 bg-indigo-50 border border-indigo-200">
              <p className="text-xs font-semibold text-indigo-800 mb-0.5">🧬 Digital Twin — professional avatar</p>
              <p className="text-xs text-gray-600">Create a reusable hyper-realistic avatar from your own video footage.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-16" />
      <Footer />
    </div>
  );
};

export default AIVideoHub;
